import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageHeader } from '../ui/PageHeader';
import { ErrorBanner } from '../ui/ErrorBanner';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonRows } from '../ui/Skeleton';
import { ConfirmModal } from '../components/ConfirmModal';
import { useToast } from '../lib/useToast';
import { useEscToClose } from '../lib/useEscToClose';
import { timeAgo } from '../lib/timeAgo';
import { supabase } from '../lib/supabase';
import {
  CANONICAL_MAX_LENGTH,
  MATCH_COPY,
  TAG_MODERATION_BINDING,
  TagModerationUnavailable,
  buildClusters,
  clusterKey,
  criterionRank,
  decideTags,
  findMatches,
  loadTagWorkspace,
  primaryMatch,
  suggestCanonical,
  validateCanonical,
  type PendingTagRow,
  type TagMatch,
  type TagWorkspace,
  type VocabEntry,
} from '../lib/tagModeration';

/**
 * Tag Approval — the queue for tags people TYPE themselves.
 *
 * Sits directly under Photo Approval in the sidebar because it is the same job: something a
 * user submitted is waiting to become part of what everyone else sees, and one person decides.
 * The structure, the class recipes, the toast, the skeleton, the empty state and the keyboard
 * flow are all lifted from `PhotoApprovalScreen` on purpose — a second review queue that looks
 * and drives like the first one costs nothing to learn.
 *
 * ── What it does NOT copy ────────────────────────────────────────────────────────────────
 * The photo screen has an undo bar. This one does not, and that is deliberate rather than
 * unfinished: approving with an edit creates a PERMANENT ALIAS by design ("rooftop bar" →
 * "rooftop" forever), so an "undo" that quietly deleted it would be a lie about what the
 * feature is. Instead the irreversible actions — an edit-alias, and any bulk decision — go
 * through an explicit confirm that states what will happen, and single-row approve/reject
 * stays one click so the queue is still fast.
 *
 * ── Untrusted text ───────────────────────────────────────────────────────────────────────
 * Every tag string on this screen was typed by a user. All of them reach the DOM only as React
 * text children or `title` attributes, both of which React escapes; there is no
 * `dangerouslySetInnerHTML` here. Search and filtering are entirely client-side over rows that
 * are already loaded, so no tag string is ever built into a PostgREST filter. See the header
 * of `src/lib/tagModeration.ts`.
 *
 * ── Where the data comes from ────────────────────────────────────────────────────────────
 * `src/lib/tagModeration.ts`, which probes for the moderation backend and falls back to a
 * clearly-labelled read-only Preview while the schema is still being written in the app repo.
 */

type CriterionFilter = 'all' | string;

/** Only rows with an unresolved collision, for the "clean up the near-duplicates" pass. */
type Lens = 'all' | 'dupes' | 'multi';

const LENSES: { key: Lens; label: string; title: string }[] = [
  { key: 'all', label: 'Everything', title: 'Every tag waiting on a decision' },
  {
    key: 'dupes',
    label: 'Near-duplicates',
    title: 'Tags that collide with the built-in catalogue, an approved tag, or another pending tag',
  },
  {
    key: 'multi',
    label: 'More than one person',
    title: 'Tags at least two different people have typed — the strongest demand signal in the queue',
  },
];

function matchTone(m: TagMatch): 'danger' | 'warning' | 'neutral' {
  if (!m.sameCriterion) return 'neutral';
  return m.kind === 'exact' || m.kind === 'punctuation' ? 'danger' : 'warning';
}

function peopleLabel(n: number): string {
  return n === 1 ? '1 person' : `${n} people`;
}

/** What a row needs beyond the raw record — computed once for the whole queue. */
type RowView = {
  row: PendingTagRow;
  matches: TagMatch[];
  best: TagMatch | null;
  /** Other PENDING rows that fold to the same stem under the same criterion. */
  siblings: PendingTagRow[];
};

export function TagApprovalScreen() {
  const { showSuccess, showError, toastNode } = useToast();
  /** Queue = the pending decisions (default). Approved = everything live, sortable, revocable. */
  const [screenView, setScreenView] = useState<'queue' | 'approved'>('queue');

  const [ws, setWs] = useState<TagWorkspace>({
    mode: 'preview',
    rows: [],
    vocab: [],
    approvedVocabLoaded: false,
    previewReason: null,
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [criterion, setCriterion] = useState<CriterionFilter>('all');
  const [lens, setLens] = useState<Lens>('all');
  const [query, setQuery] = useState('');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [cursor, setCursor] = useState(0);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());

  /** Edit-then-approve. `rows` is what gets approved as `value`. */
  const [editTarget, setEditTarget] = useState<{ rows: PendingTagRow[]; value: string } | null>(null);
  const [editBusy, setEditBusy] = useState(false);

  /** Bulk approve/reject confirmation. */
  const [bulk, setBulk] = useState<{ action: 'approve' | 'reject'; rows: PendingTagRow[] } | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  /**
   * The two double-click guards.
   *
   * `inFlight` holds ids currently in a decide call; `decided` holds ids this session has
   * already resolved. Both are refs, not state, because the guard has to be correct at the
   * instant of the second click — a state update that has not flushed yet would let the
   * duplicate through, and the thing it would create (a second alias, or an approve landing
   * after a reject) is silent and permanent. The decide RPC is specified to be idempotent as
   * well; this is the belt to its braces.
   */
  const inFlight = useRef<Set<string>>(new Set());
  const decided = useRef<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const next = await loadTagWorkspace();
      decided.current = new Set();
      inFlight.current = new Set();
      setBusyIds(new Set());
      setSelected(new Set());
      setWs(next);
    } catch (e: unknown) {
      setWs({ mode: 'preview', rows: [], vocab: [], approvedVocabLoaded: false, previewReason: null });
      setLoadError(e instanceof Error ? e.message : 'Could not load the tag queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /* ── Derived ──────────────────────────────────────────────────────────── */

  /**
   * Duplicate detection runs against the catalogue and the already-approved vocabulary only.
   * Pending-vs-pending collisions are handled through `siblings` instead, because comparing the
   * queue against itself makes every row its own duplicate and the warning stops meaning anything.
   */
  const externalVocab = useMemo<VocabEntry[]>(() => ws.vocab.filter((v) => v.source !== 'pending'), [ws.vocab]);

  const clusters = useMemo(() => buildClusters(ws.rows), [ws.rows]);

  const views = useMemo<RowView[]>(() => {
    return ws.rows.map((row) => {
      const matches = findMatches(row.tag, row.criterion, externalVocab);
      const siblings = (clusters.get(clusterKey(row)) ?? []).filter((r) => r.id !== row.id);
      return { row, matches, best: primaryMatch(matches), siblings };
    });
  }, [ws.rows, externalVocab, clusters]);

  const criteriaPresent = useMemo(() => {
    const set = new Set<string>();
    for (const v of views) set.add(v.row.criterion);
    return Array.from(set).sort((a, b) => criterionRank(a) - criterionRank(b) || a.localeCompare(b));
  }, [views]);

  const countsByCriterion = useMemo(() => {
    const m = new Map<string, number>();
    for (const v of views) m.set(v.row.criterion, (m.get(v.row.criterion) ?? 0) + 1);
    return m;
  }, [views]);

  const lensCounts = useMemo(() => {
    let dupes = 0;
    let multi = 0;
    for (const v of views) {
      if (v.best || v.siblings.length > 0) dupes += 1;
      if (v.row.distinctUsers > 1) multi += 1;
    }
    return { all: views.length, dupes, multi };
  }, [views]);

  const q = query.trim().toLowerCase();

  const visible = useMemo(() => {
    return views
      .filter((v) => {
        if (criterion !== 'all' && v.row.criterion !== criterion) return false;
        if (lens === 'dupes' && !v.best && v.siblings.length === 0) return false;
        if (lens === 'multi' && v.row.distinctUsers <= 1) return false;
        if (q) {
          const hay = `${v.row.tag} ${v.row.venueName ?? ''} ${v.row.city ?? ''} ${
            v.row.submittedByUsername ?? ''
          }`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        // The rows that most need a human first: many people, then collisions, then recency.
        if (b.row.distinctUsers !== a.row.distinctUsers) return b.row.distinctUsers - a.row.distinctUsers;
        const aDup = a.best ? 1 : 0;
        const bDup = b.best ? 1 : 0;
        if (aDup !== bDup) return bDup - aDup;
        const at = a.row.createdAt ? Date.parse(a.row.createdAt) : 0;
        const bt = b.row.createdAt ? Date.parse(b.row.createdAt) : 0;
        if (at !== bt) return bt - at;
        return a.row.tag.localeCompare(b.row.tag);
      });
  }, [views, criterion, lens, q]);

  useEffect(() => {
    setCursor((c) => (visible.length === 0 ? 0 : Math.min(c, visible.length - 1)));
  }, [visible.length]);

  useEffect(() => {
    setCursor(0);
  }, [criterion, lens, query]);

  const selectedRows = useMemo(() => ws.rows.filter((r) => selected.has(r.id)), [ws.rows, selected]);

  const readOnly = ws.mode === 'preview';
  const anyModalOpen = editTarget != null || bulk != null;

  /* ── Deciding ─────────────────────────────────────────────────────────── */

  const dropRows = useCallback((ids: readonly string[]) => {
    const gone = new Set(ids);
    setWs((prev) => ({ ...prev, rows: prev.rows.filter((r) => !gone.has(r.id)) }));
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.delete(id);
      return next;
    });
  }, []);

  /**
   * The single write path. Everything on this screen — one row, a bulk, an edit-alias — comes
   * through here, so the double-click guards and the optimistic removal cannot get out of sync
   * between three different call sites.
   */
  const runDecide = useCallback(
    async (rows: readonly PendingTagRow[], action: 'approve' | 'reject', canonical: string | null): Promise<boolean> => {
      const fresh = rows.filter((r) => !inFlight.current.has(r.id) && !decided.current.has(r.id));
      if (fresh.length === 0) return false;

      const ids = fresh.map((r) => r.id);
      ids.forEach((id) => inFlight.current.add(id));
      setBusyIds(new Set(inFlight.current));
      try {
        await decideTags({ ids, action, canonical });
        ids.forEach((id) => decided.current.add(id));
        dropRows(ids);

        const one = fresh.length === 1 ? fresh[0] : null;
        if (action === 'reject') {
          showSuccess(one ? `Rejected “${one.tag}”` : `Rejected ${fresh.length} tags`);
        } else if (canonical) {
          showSuccess(
            one
              ? `“${one.tag}” approved as “${canonical}” — the alias is permanent, the post is unchanged`
              : `${fresh.length} tags approved as “${canonical}” — aliases are permanent, no post was changed`,
          );
        } else {
          showSuccess(one ? `Approved “${one.tag}”` : `Approved ${fresh.length} tags`);
        }
        return true;
      } catch (e: unknown) {
        if (e instanceof TagModerationUnavailable) showError(e.message);
        else showError(e instanceof Error ? e.message : 'That decision did not go through');
        return false;
      } finally {
        ids.forEach((id) => inFlight.current.delete(id));
        setBusyIds(new Set(inFlight.current));
      }
    },
    [dropRows, showError, showSuccess],
  );

  const approveOne = useCallback(
    (row: PendingTagRow) => {
      if (readOnly) return;
      void runDecide([row], 'approve', null);
    },
    [readOnly, runDecide],
  );

  const rejectOne = useCallback(
    (row: PendingTagRow) => {
      if (readOnly) return;
      void runDecide([row], 'reject', null);
    },
    [readOnly, runDecide],
  );

  /** Open the edit sheet for one row, or for a whole near-duplicate cluster. */
  const openEdit = useCallback(
    (rows: PendingTagRow[], seed?: string) => {
      if (readOnly || rows.length === 0) return;
      setEditTarget({ rows, value: seed ?? suggestCanonical(rows) });
    },
    [readOnly],
  );

  const confirmEdit = useCallback(async () => {
    if (!editTarget) return;
    const problem = validateCanonical(editTarget.value);
    if (problem) {
      showError(problem);
      return;
    }
    setEditBusy(true);
    const ok = await runDecide(editTarget.rows, 'approve', editTarget.value);
    setEditBusy(false);
    if (ok) setEditTarget(null);
  }, [editTarget, runDecide, showError]);

  const confirmBulk = useCallback(async () => {
    if (!bulk) return;
    setBulkBusy(true);
    const ok = await runDecide(bulk.rows, bulk.action, null);
    setBulkBusy(false);
    if (ok) setBulk(null);
  }, [bulk, runDecide]);

  /* ── Selection ────────────────────────────────────────────────────────── */

  const toggleSelected = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allVisibleSelected = visible.length > 0 && visible.every((v) => selected.has(v.row.id));

  const toggleSelectAllVisible = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev);
      const every = visible.length > 0 && visible.every((v) => next.has(v.row.id));
      for (const v of visible) {
        if (every) next.delete(v.row.id);
        else next.add(v.row.id);
      }
      return next;
    });
  }, [visible]);

  const selectCluster = useCallback((view: RowView) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.add(view.row.id);
      for (const s of view.siblings) next.add(s.id);
      return next;
    });
  }, []);

  /* ── Keyboard ─────────────────────────────────────────────────────────── */

  useEffect(() => {
    if (anyModalOpen || loading) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const move = (delta: number) => {
        e.preventDefault();
        setCursor((c) => Math.max(0, Math.min(visible.length - 1, c + delta)));
      };
      if (e.key === 'ArrowDown' || e.key === 'j') return move(1);
      if (e.key === 'ArrowUp' || e.key === 'k') return move(-1);

      const view = visible[cursor];
      if (!view || readOnly) return;
      if (busyIds.has(view.row.id)) return;

      if (e.key === 'a' || e.key === 'Enter') {
        e.preventDefault();
        approveOne(view.row);
      } else if (e.key === 'r') {
        e.preventDefault();
        rejectOne(view.row);
      } else if (e.key === 'e') {
        e.preventDefault();
        openEdit([view.row], view.best?.sameCriterion ? view.best.existing : undefined);
      } else if (e.key === 'x' || e.key === ' ') {
        e.preventDefault();
        toggleSelected(view.row.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    anyModalOpen,
    loading,
    visible,
    cursor,
    readOnly,
    busyIds,
    approveOne,
    rejectOne,
    openEdit,
    toggleSelected,
  ]);

  const closeEdit = useCallback(() => {
    if (!editBusy) setEditTarget(null);
  }, [editBusy]);
  useEscToClose(editTarget != null, closeEdit);

  /* ── Render ───────────────────────────────────────────────────────────── */

  const editProblem = editTarget ? validateCanonical(editTarget.value) : null;
  const editMatches = editTarget
    ? findMatches(editTarget.value, editTarget.rows[0]?.criterion ?? 'Other', externalVocab)
    : [];
  const editExact = editMatches.find((m) => m.sameCriterion && (m.kind === 'exact' || m.kind === 'punctuation')) ?? null;

  return (
    <div className="admin-page">
      <PageHeader
        title="Tag Approval"
        subtitle={
          <>
            Tags people typed themselves, waiting to become part of what everyone sees. Catalogue tags apply
            immediately and never appear here. {lensCounts.all} waiting · {lensCounts.dupes} collide with a tag that
            already exists · {lensCounts.multi} typed by more than one person.
          </>
        }
        actions={
          <button
            type="button"
            className="admin-btn admin-btn-secondary admin-btn-sm"
            onClick={() => void load()}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        }
      />

      <div className="mb-5 flex gap-2">
        <button type="button" className="admin-pill" data-active={screenView === 'queue'} onClick={() => setScreenView('queue')}>
          Queue
        </button>
        <button type="button" className="admin-pill" data-active={screenView === 'approved'} onClick={() => setScreenView('approved')}>
          Approved
        </button>
      </div>

      {screenView === 'approved' ? (
        <ApprovedTagsSection showSuccess={showSuccess} showError={showError} />
      ) : (
        <>
      {loadError ? <ErrorBanner className="mb-4">{loadError}</ErrorBanner> : null}

      {/* Preview banner — the schema this screen needs is still being written. */}
      {!loading && readOnly ? (
        <div className="mb-5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-[12px] leading-relaxed text-amber-200">
          <div className="mb-1 font-semibold">Read-only preview — the moderation backend is not connected yet.</div>
          <p className="mb-2 text-amber-200/85">
            {ws.previewReason} Until it exists, this screen reads the typed-tag corpus through{' '}
            <code className="admin-chip-id">{TAG_MODERATION_BINDING.aggregateRpc}</code> (which is live) so the
            duplicate detection can be judged against real strings. Every decision is disabled — there is nothing to
            decide against yet.
          </p>
          <p className="text-amber-200/70">
            It becomes live on the next refresh, with no code change, as soon as{' '}
            <code className="admin-chip-id">{TAG_MODERATION_BINDING.queueRpc}</code> and{' '}
            <code className="admin-chip-id">{TAG_MODERATION_BINDING.decideRpc}</code> exist. If the schema landed under
            different names, point <code className="admin-chip-id">TAG_MODERATION_BINDING</code> in{' '}
            <code className="admin-chip-id">src/lib/tagModeration.ts</code> at the real ones.
          </p>
        </div>
      ) : null}

      {!loading && !readOnly && !ws.approvedVocabLoaded ? (
        <div className="mb-5 rounded-lg border border-border bg-surface px-4 py-2.5 text-[11px] leading-relaxed text-mutedDim">
          Duplicate detection is running against the built-in catalogue and the rest of this queue only —{' '}
          <code className="admin-chip-id">{TAG_MODERATION_BINDING.approvedRpc}</code> is not available, so tags approved
          in an earlier session are not being checked against.
        </div>
      ) : null}

      {/* Criterion filter */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button type="button" className="admin-pill" data-active={criterion === 'all'} onClick={() => setCriterion('all')}>
          All criteria <span className="text-mutedDim">{views.length}</span>
        </button>
        {criteriaPresent.map((c) => (
          <button key={c} type="button" className="admin-pill" data-active={criterion === c} onClick={() => setCriterion(c)}>
            {c} <span className="text-mutedDim">{countsByCriterion.get(c) ?? 0}</span>
          </button>
        ))}
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by tag, venue, city or submitter…"
          className="admin-input ml-auto w-full sm:w-72"
        />
      </div>

      {/* Lens */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {LENSES.map((l) => (
          <button
            key={l.key}
            type="button"
            title={l.title}
            className="admin-pill"
            data-active={lens === l.key}
            onClick={() => setLens(l.key)}
          >
            {l.label} <span className="text-mutedDim">{lensCounts[l.key]}</span>
          </button>
        ))}
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-mutedDim">
        <span className="admin-eyebrow">Keyboard</span>
        <span>
          <kbd className="admin-chip-id">↑</kbd> <kbd className="admin-chip-id">↓</kbd> move
        </span>
        <span>
          <kbd className="admin-chip-id">A</kbd> approve
        </span>
        <span>
          <kbd className="admin-chip-id">E</kbd> edit &amp; approve
        </span>
        <span>
          <kbd className="admin-chip-id">R</kbd> reject
        </span>
        <span>
          <kbd className="admin-chip-id">X</kbd> select
        </span>
      </div>

      {loading ? (
        <SkeletonRows rows={10} />
      ) : visible.length === 0 ? (
        <EmptyState
          title={views.length === 0 ? 'Queue is clear' : 'Nothing in this view'}
          hint={
            views.length === 0
              ? 'Nobody has typed a tag that still needs a decision. Catalogue tags never reach this queue.'
              : 'No pending tag matches these filters — clear them to see the rest.'
          }
        />
      ) : (
        <>
          <div className="mb-2 flex items-center gap-3 text-[11px] text-mutedDim">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                className="accent-accent"
                checked={allVisibleSelected}
                onChange={toggleSelectAllVisible}
                disabled={readOnly}
              />
              Select all {visible.length} shown
            </label>
          </div>

          <div className="space-y-2">
            {visible.map((view, idx) => {
              const { row, best, siblings } = view;
              const active = idx === cursor;
              const busy = busyIds.has(row.id);
              const isSelected = selected.has(row.id);
              return (
                <div
                  key={row.id}
                  onMouseEnter={() => setCursor(idx)}
                  data-selected={isSelected}
                  className={`admin-card !p-3 transition-shadow ${active ? 'ring-2 ring-accent' : ''} ${
                    isSelected ? 'bg-surfaceElevated' : ''
                  }`}
                >
                  <div className="flex flex-wrap items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1 accent-accent"
                      checked={isSelected}
                      disabled={readOnly}
                      onChange={() => toggleSelected(row.id)}
                      aria-label={`Select ${row.tag}`}
                    />

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        {/* React escapes this text node — the tag is untrusted user input. */}
                        <span className="text-[14px] font-semibold text-textPrimary" title={row.tag}>
                          {row.tag}
                        </span>
                        <span className="admin-badge admin-badge-neutral">{row.criterion}</span>
                        <span
                          className={`admin-badge ${
                            row.distinctUsers > 1 ? 'admin-badge-accent' : 'admin-badge-neutral'
                          }`}
                          title={`${row.timesAdded} submission${row.timesAdded === 1 ? '' : 's'} from ${peopleLabel(
                            row.distinctUsers,
                          )}${row.distinctVenues ? ` across ${row.distinctVenues} venue${row.distinctVenues === 1 ? '' : 's'}` : ''}`}
                        >
                          {peopleLabel(row.distinctUsers)}
                        </span>
                        {siblings.length > 0 ? (
                          <button
                            type="button"
                            className="admin-badge admin-badge-warning"
                            title="Other pending tags fold to the same thing — select the whole group"
                            onClick={() => selectCluster(view)}
                            disabled={readOnly}
                          >
                            +{siblings.length} pending variant{siblings.length === 1 ? '' : 's'}
                          </button>
                        ) : null}
                      </div>

                      <div className="mt-1 truncate text-[11px] text-mutedDim">
                        {row.venueName ? (
                          <span title={row.venueName}>{row.venueName}</span>
                        ) : (
                          <span className="italic">no venue resolved</span>
                        )}
                        {row.city ? <> · {row.city}</> : null}
                        {' · '}
                        {row.submittedByUsername
                          ? `@${row.submittedByUsername}`
                          : row.submittedBy
                            ? `${row.submittedBy.slice(0, 8)}…`
                            : 'submitter unknown'}
                        {row.createdAt ? <> · {timeAgo(row.createdAt)}</> : null}
                      </div>

                      {best ? (
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                          <span
                            className={`admin-badge ${
                              matchTone(best) === 'danger'
                                ? 'admin-badge-danger'
                                : matchTone(best) === 'warning'
                                  ? 'admin-badge-warning'
                                  : 'admin-badge-neutral'
                            }`}
                          >
                            {best.sameCriterion ? 'already exists' : `exists under ${best.criterion}`}
                          </span>
                          <span className="text-muted">
                            “{best.existing}” — {MATCH_COPY[best.kind]}
                            {best.source === 'catalogue' ? ' (built-in catalogue)' : ' (already approved)'}
                          </span>
                          {best.sameCriterion ? (
                            <button
                              type="button"
                              className="admin-btn admin-btn-ghost admin-btn-sm"
                              disabled={busy || readOnly}
                              onClick={() => openEdit([row], best.existing)}
                              title={`Approve this submission as “${best.existing}” and alias it permanently`}
                            >
                              Fold into “{best.existing}”
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    <div className="flex shrink-0 flex-wrap gap-1.5">
                      <button
                        type="button"
                        className="admin-btn admin-btn-primary admin-btn-sm"
                        disabled={busy || readOnly}
                        onClick={() => approveOne(row)}
                        title="Approve exactly as typed (A)"
                      >
                        {busy ? '…' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                        disabled={busy || readOnly}
                        onClick={() => openEdit([row], best?.sameCriterion ? best.existing : undefined)}
                        title="Approve as a different spelling and alias this one to it (E)"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn-ghost admin-btn-sm"
                        disabled={busy || readOnly}
                        onClick={() => rejectOne(row)}
                        title="Reject (R)"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Bulk bar ────────────────────────────────────────────────────── */}
      {selectedRows.length > 0 && !readOnly ? (
        <div className="fixed bottom-6 left-1/2 z-[55] flex max-w-[92vw] -translate-x-1/2 flex-wrap items-center gap-2 rounded-lg border border-border bg-surface px-4 py-2.5 shadow-lg shadow-black/40">
          <span className="text-[12px] text-textPrimary">
            {selectedRows.length} selected
          </span>
          <button
            type="button"
            className="admin-btn admin-btn-primary admin-btn-sm"
            onClick={() => setBulk({ action: 'approve', rows: selectedRows })}
          >
            Approve all
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-secondary admin-btn-sm"
            onClick={() => openEdit(selectedRows)}
            title="Approve every selected tag as one canonical spelling, aliasing the rest to it"
          >
            Merge into one…
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-ghost admin-btn-sm"
            onClick={() => setBulk({ action: 'reject', rows: selectedRows })}
          >
            Reject all
          </button>
          <button
            type="button"
            aria-label="Clear selection"
            className="text-xs text-mutedDim hover:text-white"
            onClick={() => setSelected(new Set())}
          >
            ✕
          </button>
        </div>
      ) : null}

      {/* ── Edit-then-approve ───────────────────────────────────────────── */}
      {editTarget ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
          role="presentation"
          onClick={closeEdit}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="tag-edit-title"
            className="w-full max-w-lg rounded-xl border border-border bg-surface p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="tag-edit-title" className="mb-1 text-[15px] font-semibold text-textPrimary">
              {editTarget.rows.length === 1 ? 'Edit and approve' : `Merge ${editTarget.rows.length} tags into one`}
            </h3>
            <p className="mb-4 text-[12px] leading-relaxed text-mutedDim">
              The canonical form below is what everyone will see. Each submission below keeps the words its author
              actually typed — <strong className="text-muted">no post is edited</strong> — and a permanent alias makes
              it count toward the canonical tag, so the same spelling never enters this queue again.
            </p>

            <label htmlFor="tag-canonical" className="admin-label">
              Canonical tag
            </label>
            <input
              id="tag-canonical"
              autoFocus
              className="admin-input w-full"
              value={editTarget.value}
              maxLength={CANONICAL_MAX_LENGTH}
              onChange={(e) => setEditTarget((prev) => (prev ? { ...prev, value: e.target.value } : prev))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !editBusy && !editProblem) void confirmEdit();
              }}
            />
            <div className="mt-1.5 flex items-center justify-between text-[11px]">
              <span className={editProblem ? 'text-red-300' : 'text-mutedDim'}>
                {editProblem ?? `${editTarget.value.trim().length}/${CANONICAL_MAX_LENGTH}`}
              </span>
              {editExact ? (
                <span className="text-green-300" title={`Folding into the existing “${editExact.existing}”`}>
                  matches the existing “{editExact.existing}”
                </span>
              ) : editMatches.some((m) => m.sameCriterion) ? (
                <span className="text-amber-300">
                  close to “{editMatches.find((m) => m.sameCriterion)?.existing}”
                </span>
              ) : (
                <span className="text-mutedDim">new tag</span>
              )}
            </div>

            <div className="mt-4">
              <div className="admin-eyebrow mb-2">
                What gets aliased ({editTarget.rows.length})
              </div>
              <div className="max-h-44 space-y-1 overflow-y-auto rounded-lg border border-border/80 p-2">
                {editTarget.rows.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 text-[12px]">
                    <span className="min-w-0 truncate text-textPrimary" title={r.tag}>
                      {r.tag}
                    </span>
                    <span className="shrink-0 text-[11px] text-mutedDim">
                      {r.criterion} · {peopleLabel(r.distinctUsers)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="admin-btn admin-btn-secondary" disabled={editBusy} onClick={closeEdit}>
                Cancel
              </button>
              <button
                type="button"
                className="admin-btn admin-btn-primary"
                disabled={editBusy || editProblem != null}
                onClick={() => void confirmEdit()}
              >
                {editBusy ? 'Working…' : `Approve as “${editTarget.value.trim() || '…'}”`}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        open={bulk != null}
        busy={bulkBusy}
        danger={bulk?.action === 'reject'}
        title={bulk?.action === 'reject' ? `Reject ${bulk.rows.length} tags?` : `Approve ${bulk?.rows.length ?? 0} tags?`}
        message={
          bulk?.action === 'reject'
            ? `These ${bulk.rows.length} tags stay hidden from everyone except the person who typed them. They will not come back into this queue.`
            : `These ${bulk?.rows.length ?? 0} tags go live exactly as typed — no alias is created and nothing is merged. Use “Merge into one” instead if any of them are variants of the same thing.`
        }
        confirmLabel={bulk?.action === 'reject' ? 'Reject them' : 'Approve them'}
        onConfirm={() => void confirmBulk()}
        onCancel={() => {
          if (!bulkBusy) setBulk(null);
        }}
      />

        </>
      )}

      {toastNode}
    </div>
  );
}

/* ── Approved tags: sortable, revocable (Evan 2026-10-05: "sort by approved and revoke") ─────────
   Reads `admin_list_thought_tag_queue(p_status:'approved')` (verified in prod; rows carry
   reviewed_at). Revoke = `admin_decide_thought_tag([id],'reject')` — the function accepts reject
   on an approved row and reprojects the logs that used it, so the tag disappears everywhere. */
type ApprovedRow = {
  id: string;
  tag: string;
  criterion: string;
  log_count: number | null;
  distinct_user_count: number | null;
  reviewed_at: string | null;
};

function ApprovedTagsSection({
  showSuccess,
  showError,
}: {
  showSuccess: (m: string) => void;
  showError: (m: string) => void;
}) {
  const [rows, setRows] = useState<ApprovedRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [sort, setSort] = useState<'new' | 'old' | 'az'>('new');
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmRow, setConfirmRow] = useState<ApprovedRow | null>(null);

  const loadApproved = useCallback(async () => {
    setErr(null);
    const { data, error } = await supabase.rpc('admin_list_thought_tag_queue', {
      p_status: 'approved',
      p_limit: 500,
      p_offset: 0,
    });
    if (error) {
      setErr(error.message);
      setRows([]);
      return;
    }
    const raw = Array.isArray(data) ? (data as Record<string, unknown>[]) : [];
    setRows(
      raw
        .filter((r) => typeof r.id === 'string' && typeof r.tag === 'string')
        .map((r) => ({
          id: String(r.id),
          tag: String(r.tag),
          criterion: String(r.criterion ?? ''),
          log_count: typeof r.log_count === 'number' ? r.log_count : null,
          distinct_user_count: typeof r.distinct_user_count === 'number' ? r.distinct_user_count : null,
          reviewed_at: typeof r.reviewed_at === 'string' ? r.reviewed_at : null,
        })),
    );
  }, []);

  useEffect(() => {
    void loadApproved();
  }, [loadApproved]);

  const revoke = useCallback(
    async (row: ApprovedRow) => {
      setBusyId(row.id);
      try {
        const { error } = await supabase.rpc('admin_decide_thought_tag', {
          p_ids: [row.id],
          p_action: 'reject',
        });
        if (error) throw new Error(error.message);
        setRows((prev) => (prev ? prev.filter((r) => r.id !== row.id) : prev));
        showSuccess(`Revoked “${row.tag}” — it no longer appears anywhere`);
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Revoke failed');
      } finally {
        setBusyId(null);
        setConfirmRow(null);
      }
    },
    [showError, showSuccess],
  );

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = (rows ?? []).filter(
      (r) => !needle || r.tag.toLowerCase().includes(needle) || r.criterion.toLowerCase().includes(needle),
    );
    if (sort === 'az') filtered.sort((a, b) => a.tag.localeCompare(b.tag));
    else if (sort === 'new') filtered.sort((a, b) => (b.reviewed_at ?? '').localeCompare(a.reviewed_at ?? ''));
    else filtered.sort((a, b) => (a.reviewed_at ?? '\uffff').localeCompare(b.reviewed_at ?? '\uffff'));
    return filtered;
  }, [rows, q, sort]);

  return (
    <section>
      {err ? <ErrorBanner className="mb-4">{err}</ErrorBanner> : null}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as 'new' | 'old' | 'az')}
          className="admin-input h-8 w-auto py-0 text-xs"
          aria-label="Sort approved tags"
        >
          <option value="new">Newest approved</option>
          <option value="old">Oldest approved</option>
          <option value="az">A–Z</option>
        </select>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by tag or criterion"
          className="admin-input ml-auto w-56 text-xs"
        />
      </div>
      {rows === null ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="admin-skeleton h-9 w-full" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <EmptyState title="No approved tags" hint="Tags you approve from the queue appear here, newest first." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-surface">
          <table className="w-full min-w-[640px] text-left text-xs">
            <thead className="border-b border-border text-[10px] uppercase tracking-[0.08em] text-mutedDim">
              <tr>
                <th className="px-4 py-2.5 font-semibold">Tag</th>
                <th className="px-4 py-2.5 font-semibold">Criterion</th>
                <th className="px-4 py-2.5 font-semibold">Used in</th>
                <th className="px-4 py-2.5 font-semibold">Approved</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id} className="border-b border-border/60 last:border-0">
                  <td className="px-4 py-2.5 text-[13px] text-textPrimary">{r.tag}</td>
                  <td className="px-4 py-2.5 text-muted">{r.criterion || '—'}</td>
                  <td className="px-4 py-2.5 text-muted">
                    {r.log_count ?? 0} log{(r.log_count ?? 0) === 1 ? '' : 's'}
                    {r.distinct_user_count ? ` · ${r.distinct_user_count} ${r.distinct_user_count === 1 ? 'person' : 'people'}` : ''}
                  </td>
                  <td className="px-4 py-2.5 text-mutedDim">{r.reviewed_at ? timeAgo(r.reviewed_at) : '—'}</td>
                  <td className="px-4 py-2.5 text-right">
                    {confirmRow?.id === r.id ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-[11px] text-mutedDim">Remove everywhere?</span>
                        <button
                          type="button"
                          disabled={busyId === r.id}
                          onClick={() => void revoke(r)}
                          className="rounded border border-red-500/40 px-2 py-1 text-[11px] text-red-300 hover:bg-red-500/10 disabled:opacity-50"
                        >
                          {busyId === r.id ? 'Revoking…' : 'Yes, revoke'}
                        </button>
                        <button type="button" className="text-[11px] text-mutedDim underline" onClick={() => setConfirmRow(null)}>
                          cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmRow(r)}
                        className="text-[11px] text-mutedDim underline hover:text-red-300"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
